import create from 'zustand';

export enum FileUploadStatus {
  done = 'done',
  error = 'error',
  waiting = 'waiting',
  uploading = 'uploading',
}

export type FileUploadState = {
  key: string;
  file: File;
  status: FileUploadStatus;
  progress: number | string;
  result?: any;
  options?: any;
};

export type FilesUploadStore = {
  values: FileUploadState[];
  getValues: () => FileUploadState[];
  clear: () => void;
  set: (key: string, value: FileUploadState) => void;
  update: (key: string, value: Partial<FileUploadState>) => void;
  remove: (key: string) => void;
};

type ScopeSetState = (updater: () => Partial<FilesUploadStore>) => void;
type ScopedFilesUploadStore<T extends string> = Record<T, FilesUploadStore>;

const createFlatStore = () => create<FilesUploadStore>(() => ({} as FilesUploadStore));
const createScopedStore = <T extends string>() =>
  create<ScopedFilesUploadStore<T>>(() => ({} as ScopedFilesUploadStore<T>));

type FlatStoreApi = ReturnType<typeof createFlatStore>;
type ScopedStoreApi<T extends string> = ReturnType<typeof createScopedStore<T>>;

function filesUploadStoreFactory(): FlatStoreApi;
function filesUploadStoreFactory<T extends string>(scopes: readonly T[]): ScopedStoreApi<T>;
function filesUploadStoreFactory<T extends string>(
  scopes?: readonly T[],
): FlatStoreApi | ScopedStoreApi<T> {
  const createScope = (_setState: ScopeSetState, _getState: () => FilesUploadStore) => {
    let files: Record<string | number, FileUploadState> = {};
    return {
      values: [],
      getValues: () => _getState().values,
      clear: () => {
        files = {};
        _setState(() => ({ values: [] }));
      },
      set: (key: string, value: FileUploadState) => {
        files = { ...files, [key]: value };
        _setState(() => ({ values: Object.values(files) }));
      },
      update: (key: string, value: Partial<FileUploadState>) => {
        if (!(key in files)) return;
        files = { ...files, [key]: { ...files[key], ...value } };
        _setState(() => ({ values: Object.values(files) }));
      },
      remove: (key: string) => {
        if (!(key in files)) return;
        const { [key]: _, ...rest } = files;
        files = rest;
        _setState(() => ({ values: Object.values(files) }));
      },
    };
  };

  if (!scopes?.length) {
    return create<FilesUploadStore>((setState, getState) => createScope(setState, getState));
  }

  return create<ScopedFilesUploadStore<T>>((setState, getState) => {

    const scopedFiles = {} as ScopedFilesUploadStore<T>;

    scopes.forEach((scope) => {
      const typedScope = scope as T;
      scopedFiles[typedScope] = createScope((updater) => {
        setState((state) => ({ ...state, [typedScope]: { ...state[typedScope], ...updater() } }));
      }, () => getState()[typedScope]);
    });

    return scopedFiles;
  });
}

export default filesUploadStoreFactory;
